<template>
  <Layout>
    <PageHeader
      :title="$route.params.id ? $t('setting.shift.detailShift') : $t('setting.shift.addShift')"
      :subTitle="$t('setting.shift.detailShiftDesc')"
    >
      <template #afterTitle>
        <div class="d-flex gap-2">
          <McButton
            variant="secondary"
            icon="icon-fill_close"
            :text="$t('global.cancel')"
            @click="handleCancel"
          />
          <McButton
            variant="primary"
            type="submit"
            icon="icon-outline_save"
            :text="$t('global.save')"
            :disabled="hasOverlappingShift"
            @click="handleSubmit"
            v-if="!hasNoAccess"
          />
        </div>
      </template>
    </PageHeader>

    <!-- Timeline Calendar -->
    <McShiftTimeLine
      :shifts="transformedShifts"
      :show-preview="true"
      :preview-days="form.selectedDays"
      :preview-start="form.startTime"
      :preview-end="form.endTime"
      :preview-color="form.color"
      :has-conflict="hasOverlappingShift"
    />

    <div class="bg-white p-4 rounded shadow-sm">
      <!-- Row 1: Shift Name + Time -->
      <div class="row mb-3">
        <div class="col-md-6">
          <label class="formulate-input-label">
            {{ $t('setting.shift.shiftName') }}
          </label>
          <div class="mc-shift-input-group">
            <input
              v-model="form.name"
              type="text"
              placeholder="Nama Shift"
              class="form-control mc-shift-input"
              :disabled="hasNoAccess"
              :class="{ 'is-invalid': errors.name }"
              @input="clearError('name')"
            />
            <b-dropdown
              variant="outline-secondary"
              no-caret
              right
              class="mc-shift-color-picker-dropdown"
              :disabled="hasNoAccess"
            >
              <template #button-content>
                <span
                  class="mc-shift-color-dot"
                  :style="{ backgroundColor: '#' + form.color }"
                ></span>
                <i class="mdi mdi-chevron-down"></i>
              </template>
              <div class="mc-shift-color-picker-content">
                <div class="mc-shift-color-grid">
                  <span
                    v-for="color in colorOptions"
                    :key="color"
                    class="mc-shift-color-dot mc-shift-color-dot--clickable"
                    :class="{ 'mc-shift-color-dot--selected': form.color === color }"
                    :style="{ backgroundColor: '#' + color }"
                    @click="selectColor(color)"
                  ></span>
                  <label
                    class="mc-shift-color-dot mc-shift-color-dot--clickable mc-shift-color-dot--rainbow"
                  >
                    <input
                      v-model="form.color"
                      type="color"
                      class="mc-shift-color-input"
                      :disabled="hasNoAccess"
                      @input="onCustomColor"
                    />
                  </label>
                </div>
              </div>
            </b-dropdown>
          </div>
          <small v-if="errors.name" class="mc-shift-error-text">{{ errors.name }}</small>
        </div>

        <div class="mc-shift-time-picker col-md-6">
          <div class="col-md-5">
            <label class="formulate-input-label">
              {{ $t('global.object.startTime') }}
            </label>
            <FormulateInput
              v-model="form.startTime"
              type="date-picker"
              format="HH:mm"
              value-type="HH:mm"
              placeholder="00:00"
              outer-class="mb-0"
              datepickerType="time"
              icon="icon-outline_clock"
              :disabled="hasNoAccess"
              :class="{ 'is-invalid': errors.startTime || hasOverlappingShift }"
              @input="clearError('startTime')"
            />
            <small v-if="errors.startTime" class="mc-shift-error-text">{{
              errors.startTime
            }}</small>
            <small v-else-if="hasOverlappingShift" class="mc-shift-error-text">
              {{ $t('setting.shift.validation.timeExisted') }}
            </small>
          </div>

          <div class="mc-shift-time-arrow col-md-2">
            <i class="mdi mdi-arrow-right fs-4 text-muted"></i>
          </div>

          <div class="col-md-5">
            <label class="formulate-input-label">
              {{ $t('global.object.endTime') }}
            </label>
            <FormulateInput
              v-model="form.endTime"
              type="date-picker"
              format="HH:mm"
              value-type="HH:mm"
              placeholder="00:00"
              outer-class="mb-0"
              datepickerType="time"
              icon="icon-outline_clock"
              :disabled="hasNoAccess"
              :class="{ 'is-invalid': errors.endTime || hasOverlappingShift }"
              @input="clearError('endTime')"
            />
            <small v-if="errors.endTime" class="mc-shift-error-text">{{ errors.endTime }}</small>
            <small v-else-if="hasOverlappingShift" class="mc-shift-error-text">
              {{ $t('setting.shift.validation.timeExisted') }}
            </small>
          </div>
        </div>
      </div>

      <!-- Row 2: Day Selection -->
      <div class="row">
        <div class="col-12">
          <label class="formulate-input-label">
            {{ $t('setting.shift.repetition') }}
          </label>
          <div class="mc-modal__filter-button-group" :class="{ 'is-invalid': errors.selectedDays }">
            <button
              v-for="day in weekdays"
              :key="day.key"
              type="button"
              :class="{
                'mc-modal__filter-button--active': form.selectedDays.includes(day.key),
                'mc-modal__filter-button--error': errors.selectedDays,
              }"
              class="mc-modal__filter-button"
              :disabled="hasNoAccess"
              @click="toggleDay(day.key)"
            >
              {{ day.label }}
            </button>
          </div>
          <small v-if="errors.selectedDays" class="mc-shift-error-text">{{
            errors.selectedDays
          }}</small>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2.vue'
import Layout from '@/router/layouts/main'
import McShiftTimeLine from './components/McShiftTimeLine.vue'
import McButton from '@/components/McButton'
import {
  CREATE_SHIFT,
  GET_SHIFT,
  GET_SHIFTS,
  UPDATE_SHIFT,
} from '@/services/api/setting/shift/shift'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

export default {
  page: {
    title: 'Shift Form',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    PageHeader,
    Layout,
    McShiftTimeLine,
    McButton,
  },

  mixins: [Helper],

  data() {
    return {
      form: {
        name: '',
        startTime: '',
        endTime: '',
        selectedDays: [],
        color: 'F9A825',
      },
      errors: {
        name: '',
        startTime: '',
        endTime: '',
        selectedDays: '',
      },
      existingShifts: [],
      colorOptions: [
        'BDBDBD',
        'FF9800',
        'FDD835',
        '66BB6A',
        '42A5F5',
        'EC407A',
        'AB47BC',
        '1E88E5',
        '6D4C41',
      ],
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),

    hasAccessCreate() {
      return this.hasAccess('c17a-default-shift:create')
    },
    hasAccessEdit() {
      return this.hasAccess('c17a-default-shift:update')
    },

    weekdays() {
      return [
        { key: 1, label: this.$t('global.days.monday') },
        { key: 2, label: this.$t('global.days.tuesday') },
        { key: 3, label: this.$t('global.days.wednesday') },
        { key: 4, label: this.$t('global.days.thursday') },
        { key: 5, label: this.$t('global.days.friday') },
        { key: 6, label: this.$t('global.days.saturday') },
        { key: 7, label: this.$t('global.days.sunday') },
      ]
    },

    hasOverlappingShift() {
      if (!this.form.startTime || !this.form.endTime || !this.form.selectedDays?.length) {
        return false
      }

      const newStart = this.timeToMinutes(this.form.startTime)
      const newEnd = this.timeToMinutes(this.form.endTime)
      const isNewOvernight = newEnd <= newStart

      return this.transformedShifts.some((shift) => {
        const shiftStart = this.timeToMinutes(shift.startTime)
        const shiftEnd = this.timeToMinutes(shift.endTime)
        const isShiftOvernight = shift.isOvernight || shiftEnd <= shiftStart

        return this.form.selectedDays.some((selectedDay) => {
          const nextDay = selectedDay === 7 ? 1 : selectedDay + 1
          const prevDay = selectedDay === 1 ? 7 : selectedDay - 1

          if (!isNewOvernight && !isShiftOvernight) {
            if (shift.days.includes(selectedDay)) {
              return newStart < shiftEnd && newEnd > shiftStart
            }
          }

          if (isNewOvernight && !isShiftOvernight) {
            if (shift.days.includes(selectedDay)) {
              if (newStart < shiftEnd) return true
            }
            if (shift.days.includes(nextDay)) {
              if (newEnd > shiftStart) return true
            }
          }

          if (!isNewOvernight && isShiftOvernight) {
            if (shift.days.includes(selectedDay)) {
              if (newEnd > shiftStart) return true
            }
            if (shift.days.includes(prevDay)) {
              if (newStart < shiftEnd) return true
            }
          }

          if (isNewOvernight && isShiftOvernight) {
            if (shift.days.includes(selectedDay)) {
              if (newStart < 1440 && shiftStart < 1440) return true
            }
            if (shift.days.includes(prevDay)) {
              if (newEnd > 0 && shiftEnd > 0) {
                if (newStart < shiftEnd || newEnd > shiftStart) return true
              }
            }
          }

          return false
        })
      })
    },

    transformedShifts() {
      const currentShiftId = this.$route.params.id

      const result = []

      this.existingShifts.forEach((shift) => {
        if (
          (currentShiftId && shift.shiftId == currentShiftId) ||
          !Array.isArray(shift.shiftTime) ||
          shift.shiftTime.length === 0
        ) {
          return
        }

        const st = shift.shiftTime[0]
        result.push({
          id: st.shiftTimeId || shift.shiftId,
          name: shift.name,
          color: shift.color,
          days: st.days || [],
          startTime: st.startTime?.slice(0, 5) || '',
          endTime: st.endTime?.slice(0, 5) || '',
          isOvernight: this.checkOvernight(st.startTime, st.endTime),
        })
      })

      return result
    },

    hasNoAccess() {
      return !(
        this.getAccessRight() && this.hasAccessAll(['users:create', 'c17a-default-shift:create'])
      )
    },
  },

  async mounted() {
    await this.fetchShifts()

    if (this.$route.params.id) {
      await this.getData()
    }
  },

  methods: {
    async getData() {
      const res = await this.$apollo.query({
        query: GET_SHIFT,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            shiftId: this.$route.params.id,
          },
        },
      })

      const shift = res.data?.shift?.shift

      // Handle not found
      if (!shift) {
        this.$mcToast.error(this.$t('setting.shift.notFound'))
        this.$router.push({ name: 'Setting Shift' })
        return
      }

      const shiftTime = shift.shiftTime?.[0]

      this.form = {
        name: shift.name,
        color: shift.color,
        startTime: shiftTime?.startTime?.slice(0, 5) || '',
        endTime: shiftTime?.endTime?.slice(0, 5) || '',
        selectedDays: shiftTime?.days || [],
      }
    },

    async fetchShifts() {
      const res = await this.$apollo.query({
        query: GET_SHIFTS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 100,
            },
          },
        },
      })

      const shifts = res.data?.shifts?.shifts?.data || []
      this.existingShifts = shifts
    },

    checkOvernight(start, end) {
      if (!start || !end) return false
      return end.slice(0, 5) < start.slice(0, 5)
    },
    toggleDay(day) {
      const index = this.form.selectedDays.indexOf(day)
      if (index > -1) {
        this.form.selectedDays = this.form.selectedDays.filter((d) => d !== day)
      } else {
        this.form.selectedDays = [...this.form.selectedDays, day]
        this.clearError('selectedDays')
      }
    },

    selectColor(color) {
      this.form.color = color
    },

    onCustomColor(event) {
      this.form.color = event.target.value?.slice(1)
    },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    },

    toUTC(time) {
      if (!time) return null
      const [hours, minutes] = time.split(':').map(Number)
      const date = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0))
      return date.toISOString().slice(11, 19)
    },

    clearError(field) {
      this.errors[field] = ''
    },

    validateForm() {
      this.errors.name = ''
      this.errors.startTime = ''
      this.errors.endTime = ''
      this.errors.selectedDays = ''

      if (!this.form.name || !this.form.name.trim()) {
        this.errors.name = this.$t('setting.shift.validation.shiftNameRequired')
      }

      if (!this.form.startTime) {
        this.errors.startTime = this.$t('setting.shift.validation.startTimeRequired')
      }

      if (!this.form.endTime) {
        this.errors.endTime = this.$t('setting.shift.validation.endTimeRequired')
      }

      if (!this.form.selectedDays || this.form.selectedDays.length === 0) {
        this.errors.selectedDays = this.$t('setting.shift.validation.daysRequired')
      }

      return (
        !this.errors.name &&
        !this.errors.startTime &&
        !this.errors.endTime &&
        !this.errors.selectedDays
      )
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        this.$mcToast.error(this.$t('setting.shift.validation.allFieldRequired'))
        return
      }

      if (this.hasOverlappingShift) {
        this.$mcToast.error(this.$t('setting.shift.validation.timeExisted'))
        return
      }

      const input = {
        color: this.form.color,
        name: this.form.name,
        shiftTime: {
          days: this.form.selectedDays,
          endTime: this.toUTC(this.form.endTime),
          startTime: this.toUTC(this.form.startTime),
        },
      }

      try {
        if (this.$route.params.id) {
          const res = await this.$apollo.mutate({
            mutation: UPDATE_SHIFT,
            variables: {
              input: {
                ...input,
                shiftId: this.$route.params.id,
              },
            },
          })

          if (res.data.updateShift.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.changed'))
            this.$router.push({ name: 'Setting Shift' })
          } else {
            this.$mcToast.error(this.$t('global.alert.text.failSave'))
          }
        } else {
          const res = await this.$apollo.mutate({
            mutation: CREATE_SHIFT,
            variables: { input },
          })

          if (res.data.createShift.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.added'))
            this.$router.push({ name: 'Setting Shift' })
          } else {
            this.$mcToast.error(this.$t('global.alert.text.failSave'))
          }
        }
      } catch (error) {
        console.error('Submit error:', error)
        this.$mcToast.error(this.$t('global.alert.text.failSave'))
      }
    },

    handleCancel() {
      this.$router.push({ name: 'Setting Shift' })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './components/style.scss';
</style>
